import { html } from 'htm/preact'
import { useState, useEffect } from 'preact/hooks'
import { NewsCard } from './NewsCard.js'
import { LoadingSpinner } from './LoadingSpinner.js'
import { api } from '../services/api.js'

export function Search({ query }) {
  const [articles, setArticles] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState(query || '')
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)

  useEffect(() => {
    if (query) {
      setSearchQuery(query)
      performSearch(query, 1, true)
    }
  }, [query])

  const performSearch = async (searchTerm, pageNum = 1, reset = false) => {
    if (!searchTerm.trim()) return

    try {
      setLoading(true)
      setError(null)

      const response = await api.get(`/articles/search/${encodeURIComponent(searchTerm)}`, {
        params: {
          page: pageNum,
          per_page: 20
        }
      })

      const data = response.data

      if (reset) {
        setArticles(data.articles)
        setPage(pageNum)
      } else {
        setArticles(prev => [...prev, ...data.articles])
      }

      setTotal(data.total)
      setHasMore(pageNum < data.total_pages)
    } catch (error) {
      console.error('Error searching articles:', error)
      setError('Eroare la căutarea știrilor. Încercați din nou.')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      performSearch(searchQuery.trim(), 1, true)
      setPage(1)
    }
  }

  const loadMore = () => {
    if (hasMore && !loading && searchQuery) {
      const nextPage = page + 1
      setPage(nextPage)
      performSearch(searchQuery, nextPage, false)
    }
  }

  return html`
    <div class="space-y-6">
      <!-- Search Header -->
      <div class="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          Căutare știri
        </h2>
        
        <form onSubmit=${handleSearch} class="flex gap-3">
          <input
            type="text"
            placeholder="Introduceți termenii de căutare..."
            value=${searchQuery}
            onInput=${(e) => setSearchQuery(e.target.value)}
            class="flex-1 px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg 
                   bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                   focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            required
          />
          <button
            type="submit"
            disabled=${loading}
            class="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 
                   transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                   flex items-center gap-2"
          >
            <span class=${loading ? 'animate-spin' : ''}>🔍</span>
            Caută
          </button>
        </form>
      </div>

      <!-- Search Results -->
      ${query && html`
        <div>
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              Rezultate pentru "${query}"
            </h3>
            <p class="text-gray-600 dark:text-gray-400">
              ${total} ${total === 1 ? 'rezultat găsit' : 'rezultate găsite'}
            </p>
          </div>

          ${loading && articles.length === 0 && html`
            <div class="flex justify-center items-center py-12">
              <${LoadingSpinner} />
            </div>
          `}

          ${error && html`
            <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
              <p class="text-red-600 dark:text-red-400 mb-4">${error}</p>
              <button 
                onClick=${() => performSearch(query, 1, true)}
                class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Încercați din nou
              </button>
            </div>
          `}

          ${!loading && !error && articles.length === 0 && total === 0 && html`
            <div class="text-center py-12">
              <div class="text-gray-400 text-6xl mb-4">🔍</div>
              <p class="text-gray-500 dark:text-gray-400 text-lg mb-2">
                Nu au fost găsite rezultate
              </p>
              <p class="text-gray-400 dark:text-gray-500">
                Încercați să modificați termenii de căutare
              </p>
            </div>
          `}

          ${articles.length > 0 && html`
            <div class="space-y-6">
              <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                ${articles.map(article => html`
                  <${NewsCard} 
                    key=${article.id} 
                    article=${article}
                  />
                `)}
              </div>

              ${hasMore && html`
                <div class="text-center py-6">
                  <button
                    onClick=${loadMore}
                    disabled=${loading}
                    class="px-6 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 
                           text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 
                           transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ${loading ? 'Se încarcă...' : 'Încarcă mai multe'}
                  </button>
                </div>
              `}

              ${loading && articles.length > 0 && html`
                <div class="flex justify-center py-4">
                  <${LoadingSpinner} size="small" />
                </div>
              `}
            </div>
          `}
        </div>
      `}

      ${!query && html`
        <div class="text-center py-12">
          <div class="text-gray-400 text-6xl mb-4">📰</div>
          <p class="text-gray-500 dark:text-gray-400 text-lg">
            Introduceți termenii de căutare pentru a găsi știri
          </p>
        </div>
      `}
    </div>
  `
}